import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService, ApiResponse } from './api.service';

export type TransactionType = 'expense' | 'income';
export type Currency = 'PLN' | 'EUR' | 'USD';
export type PaymentMethod = 'cash' | 'card' | 'transfer' | 'blik' | 'apple_pay';

export interface Household {
  id: number;
  name: string;
  default_currency: Currency;
  invite_code: string;
  users?: HouseholdMember[];
}

export interface HouseholdMember {
  id: number;
  name: string;
  email: string;
}

export interface Merchant {
  id: number;
  household_id: number;
  name: string;
  default_category_id: number | null;
  icon: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  default_category?: Category;
}

export interface MerchantDuplicateEntry {
  id: number;
  name: string;
  address: string | null;
  icon: string | null;
  transactions_count: number;
  default_category: Category | null;
}

export interface MerchantDuplicateGroup {
  merchants: MerchantDuplicateEntry[];
}

export interface MerchantPayload {
  name: string;
  default_category_id?: number | null;
  icon?: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface Category {
  id: number;
  household_id: number;
  parent_id: number | null;
  name: string;
  type: TransactionType;
  icon: string | null;
  color: string | null;
  is_active: boolean;
  subcategories?: Category[];
}

export interface Receipt {
  id: number;
  merchant: string | null;
  merchant_id: number | null;
  date: string;
  total: number;
  currency: Currency;
}

export interface Transaction {
  id: number;
  household_id: number;
  user_id: number;
  category_id: number | null;
  merchant_id: number | null;
  receipt_id: number | null;
  type: TransactionType;
  amount: number;
  currency: Currency;
  amount_pln: number;
  exchange_rate: number;
  description: string | null;
  merchant: string | null;
  transaction_date: string;
  payment_method: PaymentMethod | null;
  tags: string[] | null;
  is_occasional: boolean;
  occasional_source: OccasionalSource | null;
  user?: { id: number; name: string };
  category?: { id: number; name: string; type: TransactionType; icon: string | null; color: string | null };
  receipt?: Receipt;
  created_at: string;
  is_recurring?: boolean;
  recurring_instance_id?: number;
}

export type OccasionalSource = 'savings' | 'refund' | 'gift' | 'reimbursement' | 'other';

export const OCCASIONAL_SOURCES: OccasionalSource[] = ['savings', 'refund', 'gift', 'reimbursement', 'other'];

export interface TransactionFilters {
  type?: TransactionType;
  category_id?: number;
  user_id?: number;
  payment_method?: PaymentMethod;
  date_from?: string;
  date_to?: string;
  currency?: Currency;
  per_page?: number;
  page?: number;
}

export interface StoreTransactionData {
  type: TransactionType;
  amount: number;
  currency: Currency;
  category_id?: number | null;
  user_id?: number | null;
  description?: string;
  merchant?: string;
  transaction_date: string;
  payment_method?: PaymentMethod | null;
  tags?: string[];
  is_occasional?: boolean;
  occasional_source?: OccasionalSource | null;
}

export interface StoreCategoryData {
  name: string;
  type: TransactionType;
  parent_id?: number | null;
  icon?: string;
  color?: string;
}

export interface SummaryStats {
  total_income: number;
  total_expense: number;
  balance: number;
  tx_count: number;
  avg_daily_expense: number;
  days: number;
  by_category: CategoryStat[];
  by_member: MemberStat[];
  by_payment_method: PaymentMethodStat[];
  by_currency: CurrencyStat[];
  by_merchant: MerchantStat[];
  top_transactions: Transaction[];
  occasional: OccasionalStats;
}

export interface OccasionalStats {
  total: number;
  count: number;
  by_source: OccasionalSourceStat[];
  by_category: CategoryStat[];
  items: Transaction[];
}

export interface OccasionalSourceStat {
  occasional_source: OccasionalSource;
  total: number;
  count: number;
}

export interface CategoryStat {
  category_id: number | null;
  type?: TransactionType;
  total: number;
  count: number;
  category?: Category;
}

export interface MemberStat {
  user_id: number;
  type: TransactionType;
  total: number;
  count: number;
  user?: HouseholdMember;
}

export interface PaymentMethodStat {
  payment_method: PaymentMethod;
  type: TransactionType;
  total: number;
  count: number;
}

export interface CurrencyStat {
  currency: Currency;
  type: TransactionType;
  total_original: number;
  total_pln: number;
  count: number;
}

export interface MerchantStat {
  merchant_id: number | null;
  merchant: string;
  total: number;
  count: number;
  avg: number;
}

export interface TrendData {
  month: string;
  type: TransactionType;
  total: number;
  count: number;
}

export interface DailyData {
  day: string;
  type: TransactionType;
  total: number;
  count: number;
}

export interface Budget {
  id: number;
  category_id: number;
  category: Category;
  amount: number;
  period: 'monthly';
  spent: number;
  remaining: number;
  percent: number;
}

export interface StoreBudgetData {
  category_id: number;
  amount: number;
  period?: 'monthly';
}

export type RecurringFrequency = 'monthly' | 'weekly' | 'yearly';
export type RecurringEndType = 'never' | 'date' | 'count';
export type RecurringInstanceStatus = 'pending' | 'confirmed' | 'skipped';

export interface RecurringInstance {
  id: number;
  template_id: number;
  transaction_id: number | null;
  due_date: string;
  amount: number;
  status: RecurringInstanceStatus;
  transaction?: { id: number; transaction_date: string; amount: number };
}

export interface RecurringTemplate {
  id: number;
  household_id: number;
  category_id: number | null;
  user_id: number | null;
  name: string;
  type: TransactionType;
  amount: number;
  currency: Currency;
  merchant: string | null;
  description: string | null;
  payment_method: PaymentMethod | null;
  frequency: RecurringFrequency;
  day_of_month: number;
  start_date: string;
  end_type: RecurringEndType;
  end_date: string | null;
  end_count: number | null;
  confirmed_count: number;
  is_active: boolean;
  is_expired: boolean;
  category?: { id: number; name: string; type: TransactionType; icon: string | null; color: string | null };
  user?: { id: number; name: string };
  upcoming_instances: RecurringInstance[];
}

export interface StoreRecurringTemplateData {
  name: string;
  type: TransactionType;
  amount: number;
  currency: Currency;
  category_id?: number | null;
  user_id?: number | null;
  merchant?: string;
  description?: string;
  payment_method?: PaymentMethod | null;
  frequency: RecurringFrequency;
  day_of_month: number;
  start_date: string;
  end_type: RecurringEndType;
  end_date?: string | null;
  end_count?: number | null;
  is_active?: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number | null;
    to: number | null;
  };
  pending_recurring?: T[];
}

@Injectable({ providedIn: 'root' })
export class FinanceService {
  // undefined = not yet loaded, null = loaded but no household, Household = has household
  private householdSubject = new BehaviorSubject<Household | null | undefined>(undefined);
  household$ = this.householdSubject.asObservable();

  constructor(private api: ApiService) {}

  setHousehold(household: Household | null): void {
    this.householdSubject.next(household);
  }

  resetCache(): void {
    this.householdSubject.next(undefined);
  }

  // Household
  getHousehold(): Observable<ApiResponse<{ household: Household }>> {
    return this.api.get<{ household: Household }>('household').pipe(
      tap(r => this.householdSubject.next(r.data?.household ?? null))
    );
  }

  createHousehold(data: { name: string; default_currency: Currency }): Observable<ApiResponse<{ household: Household }>> {
    return this.api.post<{ household: Household }>('household', data).pipe(
      tap(r => { if (r.data?.household) this.householdSubject.next(r.data.household); })
    );
  }

  updateHousehold(data: { name: string; default_currency: Currency }): Observable<ApiResponse<{ household: Household }>> {
    return this.api.put('household', data);
  }

  joinHousehold(inviteCode: string): Observable<ApiResponse<{ household: Household }>> {
    return this.api.post<{ household: Household }>('household/join', { invite_code: inviteCode }).pipe(
      tap(r => { if (r.data?.household) this.householdSubject.next(r.data.household); })
    );
  }

  regenerateInviteCode(): Observable<ApiResponse<{ invite_code: string }>> {
    return this.api.post('household/regenerate-code', {});
  }

  inviteMember(email: string): Observable<ApiResponse> {
    return this.api.post('household/invite', { email });
  }

  // Merchants
  getMerchants(): Observable<ApiResponse<{ merchants: Merchant[] }>> {
    return this.api.get('merchants');
  }

  createMerchant(data: MerchantPayload): Observable<ApiResponse<{ merchant: Merchant }>> {
    return this.api.post('merchants', data);
  }

  updateMerchant(id: number, data: MerchantPayload): Observable<ApiResponse<{ merchant: Merchant }>> {
    return this.api.put(`merchants/${id}`, data);
  }

  deleteMerchant(id: number): Observable<ApiResponse> {
    return this.api.delete(`merchants/${id}`);
  }

  loadDefaultMerchants(): Observable<ApiResponse<{ added: number; skipped: number }>> {
    return this.api.post('merchants/load-defaults', {});
  }

  getMerchantDuplicates(threshold = 70): Observable<ApiResponse<{ groups: MerchantDuplicateGroup[]; total: number }>> {
    return this.api.get(`merchants/duplicates?threshold=${threshold}`);
  }

  mergeMerchants(keepId: number, mergeIds: number[]): Observable<ApiResponse<{ kept: Merchant; merged: number }>> {
    return this.api.post('merchants/merge', { keep_id: keepId, merge_ids: mergeIds });
  }

  // Categories
  getCategories(): Observable<ApiResponse<{ categories: Category[] }>> {
    return this.api.get('categories');
  }

  createCategory(data: StoreCategoryData): Observable<ApiResponse<{ category: Category }>> {
    return this.api.post('categories', data);
  }

  updateCategory(id: number, data: Partial<StoreCategoryData> & { is_active?: boolean }): Observable<ApiResponse<{ category: Category }>> {
    return this.api.put(`categories/${id}`, data);
  }

  deleteCategory(id: number): Observable<ApiResponse> {
    return this.api.delete(`categories/${id}`);
  }

  // Transactions
  getTransactions(filters: TransactionFilters = {}): Observable<ApiResponse<PaginatedResponse<Transaction>>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    });
    const query = params.toString();
    return this.api.get(`transactions${query ? '?' + query : ''}`);
  }

  createTransaction(data: StoreTransactionData): Observable<ApiResponse<{ transaction: Transaction }>> {
    return this.api.post('transactions', data);
  }

  batchCreateTransactions(transactions: StoreTransactionData[]): Observable<ApiResponse<{ created: Transaction[]; failed: unknown[]; summary: { total: number; created: number; failed: number } }>> {
    return this.api.post('transactions/batch', { transactions });
  }

  updateTransaction(id: number, data: Partial<StoreTransactionData>): Observable<ApiResponse<{ transaction: Transaction }>> {
    return this.api.put(`transactions/${id}`, data);
  }

  deleteTransaction(id: number): Observable<ApiResponse> {
    return this.api.delete(`transactions/${id}`);
  }

  batchDeleteTransactions(ids: number[]): Observable<ApiResponse<{ deleted: number }>> {
    return this.api.delete('transactions/batch', { body: { ids } });
  }

  // Receipts
  createReceipt(data: {
    merchant_name?: string | null;
    merchant_id?: number | null;
    date: string;
    total: number;
    currency: string;
    transactions: StoreTransactionData[];
  }): Observable<ApiResponse<{ created: Transaction[]; failed: unknown[]; summary: { total: number; created: number; failed: number } }>> {
    return this.api.post('receipts', data);
  }

  deleteReceipt(id: number): Observable<ApiResponse> {
    return this.api.delete(`receipts/${id}`);
  }

  // Budgets
  getBudgets(month?: string): Observable<ApiResponse<{ budgets: Budget[]; month: string }>> {
    return this.api.get(`budgets${month ? '?month=' + month : ''}`);
  }

  saveBudget(data: StoreBudgetData): Observable<ApiResponse<{ budget: Budget }>> {
    return this.api.post('budgets', data);
  }

  deleteBudget(id: number): Observable<ApiResponse> {
    return this.api.delete(`budgets/${id}`);
  }

  // Recurring
  getRecurring(): Observable<ApiResponse<{ templates: RecurringTemplate[] }>> {
    return this.api.get('recurring');
  }

  syncRecurring(): Observable<ApiResponse<{ synced: number }>> {
    return this.api.post('recurring/sync', {});
  }

  getRecurringTemplate(id: number): Observable<ApiResponse<{ template: RecurringTemplate }>> {
    return this.api.get(`recurring/${id}`);
  }

  createRecurring(data: StoreRecurringTemplateData): Observable<ApiResponse<{ template: RecurringTemplate }>> {
    return this.api.post('recurring', data);
  }

  updateRecurring(id: number, data: StoreRecurringTemplateData): Observable<ApiResponse<{ template: RecurringTemplate }>> {
    return this.api.put(`recurring/${id}`, data);
  }

  deleteRecurring(id: number): Observable<ApiResponse> {
    return this.api.delete(`recurring/${id}`);
  }

  confirmInstance(instanceId: number, amount?: number): Observable<ApiResponse<{ instance: RecurringInstance }>> {
    return this.api.post(`recurring/instances/${instanceId}/confirm`, amount ? { amount } : {});
  }

  confirmInstanceBatch(instanceIds: number[]): Observable<ApiResponse<{ confirmed: number }>> {
    return this.api.post('recurring/instances/confirm-batch', { instance_ids: instanceIds });
  }

  skipInstance(instanceId: number): Observable<ApiResponse<{ instance: RecurringInstance }>> {
    return this.api.post(`recurring/instances/${instanceId}/skip`, {});
  }

  updateInstance(instanceId: number, amount: number): Observable<ApiResponse<{ instance: RecurringInstance }>> {
    return this.api.put(`recurring/instances/${instanceId}`, { amount });
  }

  restoreInstance(instanceId: number): Observable<ApiResponse<{ instance: RecurringInstance }>> {
    return this.api.post(`recurring/instances/${instanceId}/restore`, {});
  }

  regenerateTemplate(templateId: number): Observable<ApiResponse> {
    return this.api.post(`recurring/${templateId}/regenerate`, {});
  }

  // Statistics
  getSummary(dateFrom: string, dateTo: string): Observable<ApiResponse<SummaryStats>> {
    return this.api.get(`statistics/summary?date_from=${dateFrom}&date_to=${dateTo}`);
  }

  getDaily(dateFrom: string, dateTo: string): Observable<ApiResponse<{ daily: DailyData[] }>> {
    return this.api.get(`statistics/daily?date_from=${dateFrom}&date_to=${dateTo}`);
  }

  getTrend(months = 12): Observable<ApiResponse<{ trend: TrendData[] }>> {
    return this.api.get(`statistics/trend?months=${months}`);
  }
}
